import React, { useEffect, useRef, useState } from 'react';
import { personalInfo } from '../data/portfolioData';

const C = {
  cyan: '#00f5ff', green: '#00ff88', purple: '#7b2fff',
  gray: '#4a6275', white: '#e8f4f8',
  border: 'rgba(0,245,255,0.12)',
  card: 'rgba(0,245,255,0.025)',
};

const terminalLines = [
  { type: 'cmd',  text: 'cat about.json' },
  { type: 'out',  text: '{' },
  { type: 'key',  text: '  "name":', value: '"Made Marshall Vira Deva"' },
  { type: 'key',  text: '  "main_role":', value: '"Web Dev & Digital Marketing"' },
  { type: 'key',  text: '  "company":', value: '"Dewanta Villas Management"' },
  { type: 'key',  text: '  "location":', value: '"Bali, Indonesia"' },
  { type: 'key',  text: '  "gpa":', value: '"3.86 / 4.00"' },
  { type: 'key',  text: '  "status":', value: '"Open to Opportunities ✓"' },
  { type: 'out',  text: '}' },
  { type: 'gap' },
  { type: 'cmd',  text: 'ls expertise/' },
  { type: 'out',  text: 'web_development/     digital_marketing/' },
  { type: 'out',  text: 'machine_learning/    llm_evaluation/' },
  { type: 'out',  text: 'data_science/        it_governance/' },
  { type: 'gap' },
  { type: 'cmd',  text: 'echo $CURRENT_FOCUS' },
  { type: 'green',text: 'Web Dev & Digital Marketing @ Dewanta Villas Management' },
];

export default function About() {
  const [visibleLines, setVisibleLines] = useState(0);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let i = 0;
    const timer = setInterval(() => {
      i++;
      setVisibleLines(i);
      if (i >= terminalLines.length) clearInterval(timer);
    }, 120);
    return () => clearInterval(timer);
  }, [inView]);

  return (
    <section id="about" ref={sectionRef} style={{ background: 'rgba(4,8,16,0.8)' }}>
      {/* Header */}
      <div style={{ marginBottom: 64 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          fontFamily: "'Space Mono', monospace", fontSize: 10,
          letterSpacing: 4, textTransform: 'uppercase', color: C.cyan, marginBottom: 12,
        }}>
          <span style={{ width: 32, height: 1, background: C.cyan, display: 'inline-block' }} />
          About Me
        </div>
        <h2 style={{
          fontFamily: "'Orbitron', monospace",
          fontSize: 'clamp(26px, 4vw, 44px)',
          fontWeight: 700, color: C.white, letterSpacing: -1,
        }}>
          Who <span style={{ color: C.cyan }}>Am I?</span>
        </h2>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 60, alignItems: 'start',
      }}>
        {/* Text side */}
        <div>
          <p style={{ fontSize: 16, lineHeight: 1.9, color: 'rgba(232,244,248,0.65)', marginBottom: 20 }}>
            I'm a fresh <strong style={{ color: C.cyan }}>Information Technology graduate</strong> from Universitas Udayana (GPA 3.86)
            with a deep passion for Artificial Intelligence, Machine Learning, and modern Software Engineering.
          </p>
          <p style={{ fontSize: 16, lineHeight: 1.9, color: 'rgba(232,244,248,0.65)', marginBottom: 20 }}>
            Currently, I work as a <strong style={{ color: C.green }}>Web Developer & Digital Marketing</strong> at Dewanta Villas Management,
            building modern websites and executing digital marketing strategies for a Bali-based villa company.
            I also take on <strong style={{ color: C.cyan }}>AI QA Trainer</strong> work at Invisible Technologies on a project basis.
          </p>
          <p style={{ fontSize: 16, lineHeight: 1.9, color: 'rgba(232,244,248,0.65)', marginBottom: 32 }}>
            In parallel, I'm pursuing my <strong style={{ color: C.cyan }}>Master's degree in Information Systems</strong> at Telkom University,
            deepening my expertise to stay at the frontier of technology.
          </p>

          {/* Info grid */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16,
          }}>
            {[
              { label: 'Degree', value: 'S.Kom — IT Graduate' },
              { label: 'GPA', value: '3.86 / 4.00' },
              { label: 'Location', value: 'Bali, Indonesia' },
              { label: 'Available', value: 'Open to Work' },
              { label: 'Languages', value: 'Bahasa, English' },
              { label: 'Focus', value: 'Web Dev / Marketing' },
            ].map((item, i) => (
              <div key={i} style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                padding: '12px 16px',
              }}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: C.gray, textTransform: 'uppercase', marginBottom: 4 }}>
                  {item.label}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.white }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Terminal */}
        <div>
          <div style={{
            background: 'rgba(0,0,0,0.7)',
            border: `1px solid ${C.border}`,
            overflow: 'hidden',
            fontFamily: "'Space Mono', monospace",
            boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
          }}>
            {/* Terminal bar */}
            <div style={{
              background: 'rgba(0,245,255,0.04)',
              padding: '10px 16px',
              display: 'flex', alignItems: 'center', gap: 8,
              borderBottom: `1px solid ${C.border}`,
            }}>
              {[['#ff5f57',''], ['#ffbd2e',''], ['#28ca41','']].map(([color], i) => (
                <div key={i} style={{ width: 11, height: 11, borderRadius: '50%', background: color }} />
              ))}
              <span style={{ fontSize: 10, color: C.gray, letterSpacing: 2, marginLeft: 'auto' }}>
                marshall@portfolio:~
              </span>
            </div>

            {/* Terminal body */}
            <div style={{ padding: '20px', minHeight: 300, fontSize: 12, lineHeight: 2.1 }}>
              {terminalLines.slice(0, visibleLines).map((line, i) => {
                if (line.type === 'gap') return <div key={i} style={{ height: 6 }} />;
                if (line.type === 'cmd') return (
                  <div key={i} style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: C.green }}>➜</span>
                    <span style={{ color: C.cyan }}>~</span>
                    <span style={{ color: C.white }}>{line.text}</span>
                  </div>
                );
                if (line.type === 'key') return (
                  <div key={i} style={{ color: 'rgba(232,244,248,0.45)', paddingLeft: 16 }}>
                    <span style={{ color: C.purple }}>{line.text}</span>
                    {' '}<span style={{ color: '#fbbf24' }}>{line.value}</span>
                    <span style={{ color: 'rgba(232,244,248,0.3)' }}>,</span>
                  </div>
                );
                if (line.type === 'green') return (
                  <div key={i} style={{ color: C.green, paddingLeft: 16 }}>{line.text}</div>
                );
                return (
                  <div key={i} style={{ color: 'rgba(232,244,248,0.45)', paddingLeft: 16 }}>{line.text}</div>
                );
              })}
              {/* Blinking cursor */}
              {visibleLines < terminalLines.length && (
                <span style={{
                  display: 'inline-block', width: 8, height: 14,
                  background: C.cyan, verticalAlign: 'middle',
                  animation: 'blink 1s step-end infinite',
                }} />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
